import { Injectable, signal } from '@angular/core';

import { jwtDecode } from 'jwt-decode';
import { AppStorage } from '../../utils/interfaces/app-storage';
import { IToken } from '../../utils/interfaces/token';
import { IUserMe } from '../../utils/interfaces/user-me';

@Injectable({
  providedIn: 'root',
})
export class SessionTokenService {
  private readonly token = signal<any>(null);
  private readonly userMe = signal<IUserMe | null>(null);

  constructor(private readonly appStorage: AppStorage) {
    this.token.set(this.appStorage.get() ?? null);
    
    const storedUserMe = localStorage.getItem('userMe');
    if (storedUserMe) {
      try {
        this.userMe.set(JSON.parse(storedUserMe));
      } catch {
        localStorage.removeItem('userMe');
      }
    }
  }

  isAuthenticatedTokenIsValid(): boolean {
    const token = this.token() ? this.decodeToken() : null;
    if (!token?.exp || new Date(token.exp * 1000) < new Date()) {
      this.closeSession();
      return false;
    }

    return true;
  }

  getToken(): string {
    return this.token();
  }

  setToken(token: string, isLoggedOffice365: boolean = false): void {
    this.token.set(token);
    this.appStorage.set(token);

    localStorage.setItem('isLoggedOffice365', isLoggedOffice365.toString());
  }

  getIsLoggedOffice365(): boolean {
    return localStorage.getItem('isLoggedOffice365') === 'true';
  }

  closeSession(): void {
    this.token.set(null);
    this.userMe.set(null);
    this.appStorage.removeToken();
    localStorage.removeItem('userMe');
    localStorage.removeItem('isLoggedOffice365');
  }

  decodeToken(): IToken {
    const token: any = jwtDecode(this.token());
    return token;
  }

  setUserMe(userMe: IUserMe): void {
    this.userMe.set(userMe);
    localStorage.setItem('userMe', JSON.stringify(userMe));
  }

  getUserMe(): IUserMe | null {
    return this.userMe();
  }

  getUserId(): string | null {
    const decoded = this.decodeToken();
    return decoded?.ID || null;
  }

  getPermissions(): string[] {
    return this.userMe()?.permissions || [];
  }

  getBranches(): { id: string; name: string }[] {
    return this.userMe()?.branches || [];
  }

  getUserRole(): string | null {
    return this.userMe()?.roleName || null;
  }

  getUserEmail(): string | null {
    return this.userMe()?.email || null;
  }

  getUserPhoto(): string | null {
    return this.userMe()?.photo || null;
  }
}
